import React, { useCallback, useEffect, useRef, useState } from "react";
import { getBudgetHistory } from "../../backend-services";
import { useProjectStore } from "../../state";
import { useExpenseSearch } from "../../hooks/expenses";
import ExpenseEmptyIcon from "../../assets/icons/InvCusExpEmptyIcon";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { servicesType } from "../../constants/Subscription";
import { TODAY, defaultShortcuts } from "../../constants/helpServices";
import DatePickerWithShortcuts from "../../components/DatePicker";
import {
  expCategories,
  mobileExpCategories,
} from "../../constants/filterCategories";
import { ICustomer, IProject } from "../../@types";
import { useTranslation } from "react-i18next";
import { SubscriptionModal } from "../../components/common/Subscription/SubscriptionModal";
import { MobileSubscriptionModal } from "../../components/common/Subscription/MobileSubscriptionModal";
import ReusableListPage from "../../components/list-page";
import NoDocument from "../../assets/icons/svgicons/NoDocuments.svg";
import ActionModal from "../../components/common/ActionModal";
import { ListInfoCardProps } from "../../components/list-page/desktop/ListInfoCard";
import ExpenseUploadComp from "../../components/expense/expenseUpload";
import PageLoader from "../../components/common/PageLoader";
import { useCompanyStore } from "../../state/useCompanyStore";
import dayjs from "dayjs";
import { columns, renderProjectsData } from "../../utils/projects/list-page";
import { CreateProject } from "./create";
import { useProjectStatsData } from "../../hooks/Stats/project";
import { useProjectHook } from "../../hooks/project/useProject";
import { DeleteProject } from "../../utils/projects/deleteProject";
import { showToast } from "../../utils/toastUtils";
import { set } from "lodash";
import { useProjectSearch } from "../../hooks/project/useProjectSearch";

const ListProjectsPage = () => {
  const { t } = useTranslation("expenses");

  const [searchParams] = useSearchParams();

  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");
  const selectedParam = searchParams.get("selectedLabel");
  const navigate = useNavigate();
  const location = useLocation();
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const [isBudgetLoading, setIsBudgetLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startOfMonth, setStartOfMonth] = useState(
    startDateParam
      ? new Date(startDateParam)
      : dayjs().subtract(30, "days").startOf("day").toDate(),
  );
  const [endOfMonth, setEndOfMonth] = useState(
    endDateParam ? new Date(endDateParam) : dayjs().endOf("day").toDate(),
  );

  const { projectData, error, fetchProjectStat } = useProjectStatsData(
    startOfMonth,
    endOfMonth,
  );

  const [projectToDelete, setProjectToDelete] = useState<IProject | null>(null);
  const [projectDeleting, setProjectDeleting] = useState(false);

  const handleDeleteClick = (project: IProject) => {
    setProjectToDelete(project);
  };

  const handleCancelDelete = () => {
    setProjectToDelete(null);
  };
  const handleModalDelete = async () => {
    setProjectDeleting(true);
    try {
      let res = await deleteProject(projectToDelete?.id || "");
      showToast("Project Deleted", "success");
      setProjectToDelete(null);
      fetchProjectStat();
      setProjectDeleting(false);
    } catch (error) {
      showToast("Something Went wrong", "error");
      setProjectDeleting(false);
    }
  };

  const { CreateProjectSideBar, openModal } = useProjectHook({
    startOfMonth,
    endOfMonth,
  });
  const {
    projects,
    fetchData,
    clearAllFilters,
    fetchMoreData,
    filter,
    hasData,
    isLoading,
    isLoadingFilter,
    isLoadingInitial,
    isLoadingMore,
    pagination,
    range,
    searchText,
    setFilter: handleFilter,
    setSearchText: onSearchData,
    deleteProject,
  } = useProjectStore();

  const {
    data: searchResult,
    isSearching,
    setSearchedDone,
    searchedDone,
    handleSearch: handleProjectSearch,
  } = useProjectSearch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] =
    React.useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>("Last 30 Days");
  const [selected, setSelected] = useState<[Date, Date] | null>(null);
  const [selectedRange, setSelectedRange] = useState([
    {
      startDate: TODAY.date,
      endDate: TODAY.date,
      key: "selection",
      label: TODAY.label,
    },
  ]);

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const handleCreateProject = () => {
    openModal();
    return;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startDateRange = params.get("startDate") || "";
    const endDateRange = params.get("endDate") || "";
    setDateRange({ startDate: startDateRange, endDate: endDateRange });
  }, [location.search]);
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (startDateParam && endDateParam && !isFetching) {
      setSelectedLabel(selectedParam || "Last 30 Days");
      setStartOfMonth(new Date(startDateParam));
      setEndOfMonth(new Date(endDateParam));
      fetchData({
        startDate: new Date(startDateParam)?.toISOString().replace(".000Z", ""),
        endDate: new Date(endDateParam)?.toISOString().replace(".000Z", ""),
      })?.finally(() => {
        navigate("/projects", { replace: true });
      });
    } else if (dateRange.startDate || dateRange.endDate) {
      fetchData({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    }
  }, [range, dateRange, searchParams]);

  const fetchAllData = useCallback(async () => {
    try {
      setIsBudgetLoading(true);
      await Promise.all([fetchData({ actionType: "initial" })]);
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data;
      }
    } finally {
      setIsBudgetLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const infoCardData = [
    {
      title: "Total Projects",
      mobileTitle: "Total Projects",
      value: projectData?.totalProjects,
      bgColor: "#F0F7FF",
      className: "w-full",
    },
    {
      title: "In Progress",
      mobileTitle: "In Progress",
      value: projectData?.totalInProgressProjects,
      bgColor: "#ECFDF3",
      className: "w-full",
    },
    {
      title: "Completed",
      mobileTitle: "Completed",
      value: projectData?.totalCompletedProjects,
      bgColor: "#FFFAEB",
      className: "w-full",
    },
    {
      title: "Cancelled",
      mobileTitle: "Cancelled",
      value: `${projectData?.totalCancelledProjects || "--"}`,
      bgColor: "#FEF3F2",
      className: "w-full",
    },
  ] as ListInfoCardProps[];

  const handleRangeSelect = async (ranges: any) => {
    setShowCalendar(false);
    setSelected(ranges);
    const startDate = ranges[0];
    const endDate = ranges[1];
    let label = "";
    for (const shortcut of defaultShortcuts) {
      const [shortcutStart, shortcutEnd] = shortcut.range();

      if (
        startDate.getTime() === shortcutStart.getTime() &&
        endDate.getTime() === shortcutEnd.getTime()
      ) {
        label = shortcut.label;
        break;
      }
    }
    if (label) {
      setSelectedLabel(label);
    } else {
      setSelectedLabel(
        `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
      );
    }
    setStartOfMonth(new Date(startDate));
    setEndOfMonth(new Date(endDate));
  };

  const handleApplyButtonClick = async (
    startDate: Date,
    endDate: Date | null,
  ) => {
    if (endDate === null) {
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
    }
    setEndDate(startDate);
    setStartDate(endDate);
    let newStartDate = startDate?.toISOString().replace(".000Z", "");
    let newEndDate = endDate?.toISOString().replace(".000Z", "");
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    try {
      await handleRangeSelect([startDate, endDate]);
      handleCalendarToggle();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  useEffect(() => {
    if (!showCalendar) {
      setSelected(null);
    }
  }, [showCalendar]);

  useEffect(() => {
    // @ts-ignore
    if (filter && dateRange.startDate == "" && dateRange.endDate == "") {
      fetchData({ actionType: "filter" });
    }
  }, [searchText, filter]);

  const handleSetCustomer = (selectedCustomer: ICustomer | null) => {
    // Update the state or form data with the selected customer
  };

  const clearAllFilter = () => {
    setDateRange({ startDate: "", endDate: "" });
    setSelectedLabel("Last 30 Days");
    setStartOfMonth(dayjs().subtract(30, "days").startOf("day").toDate());
    setEndOfMonth(dayjs().endOf("day").toDate());
    setStartDate(null);
    setEndDate(null);
    onSearchData("");
    handleFilter({ billable: null, paymentMethod: "" });
    setSelectedLabel("Last 30 Days");
    fetchData();
  };

  if (isLoadingInitial) {
    return (
      <>
        <PageLoader />
      </>
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <div className="pb-8">
        <ReusableListPage
          categories={expCategories}
          hasData={hasData}
          mobileCategories={mobileExpCategories}
          createTitle="Create Project"
          pageTitle="Project"
          showCalendar={showCalendar}
          showBudgetCard={false}
          infoCardData={infoCardData}
          handleCalendarToggle={handleCalendarToggle}
          handleCreate={handleCreateProject}
          selectedLabel={selectedLabel}
          fetchMoreData={fetchMoreData}
          onSearchData={onSearchData}
          handleFilter={handleFilter}
          handleSearch={handleProjectSearch}
          meta={pagination}
          data={renderProjectsData(projects, handleDeleteClick)}
          modules="projects"
          searchDisplayKey={"id"}
          searchPlaceHolder="Search Project Name"
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          isLoadingFilter={isLoadingFilter}
          isSearching={isSearching}
          searchResult={searchResult}
          selectedFilter={filter}
          showFilter={false}
          searchedDone={searchedDone}
          setSearchedDone={setSearchedDone}
          columns={columns}
          onSubmit={() => {}}
          mobileCard="projectCard"
          clearAllFilters={clearAllFilters}
          expenseUpload={
            <ExpenseUploadComp
              setShowUploadPopup={setShowUploadPopup}
              showUploadPopup={showUploadPopup}
            />
          }
          emptyStateDescription="Creating a project helps you keep track of what your business spends money on the most. Not sure how to create a project?"
          emptyPageIcon={<ExpenseEmptyIcon />}
          emptyPageHref="https://www.youtube.com/watch?si=MqUzjg-b6k0u767M&v=G-6sAdSDvRM&feature=youtu.be"
          emptyPageLinkName="Project"
          handleSetCustomer={handleSetCustomer}
          emptyMessage={t("expense.emptyState")}
        />
      </div>

      <div className="mt-[-30em]">
        {showSubscriptionModal && (
          <SubscriptionModal>
            <MobileSubscriptionModal
              onClose={() => setShowSubscriptionModal(false)}
              titleText={`Need to access more features?`}
              subtitleText={subscriptionErrorMessage}
            />
          </SubscriptionModal>
        )}
      </div>
      {showCalendar && (
        <>
          <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
            <div className="md:flex justify-center items-center h-full w-full">
              <DatePickerWithShortcuts
                onRangeSelect={handleRangeSelect}
                selected={selected}
                onClose={handleCalendarToggle}
                onApplyButtonClick={(startDate, endDate) =>
                  handleApplyButtonClick(startDate, endDate)
                }
                fetchData={clearAllFilter}
                endDate={endDate ?? endOfMonth}
                startDate={startDate ?? startOfMonth}
              />
            </div>
          </div>
        </>
      )}

      <CreateProjectSideBar close={fetchData} />
      {projectToDelete && (
        <DeleteProject
          actionFn={() => {
            handleModalDelete();
          }}
          cancelFn={handleCancelDelete}
          subTitle={`You are about to delete ${projectToDelete.name}. Are you sure you want to
                continue this action?`}
          title="Delete Project"
          actionText={projectDeleting ? "Deleting..." : "Yes, Delete"}
          cancelText="Cancel"
        />
      )}
    </>
  );
};

export { ListProjectsPage };
